import ListingCardTypeEnum from '@models/enums/ListingCardTypeEnum';
import { IKitDropdownRef } from '@models/IKitDropdownRef';
import { IProduct } from '@models/IProduct';
import { ListingProductType } from '@models/IProductListing';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import KitDropdown from '@molecules/KitDropdown/KitDropdown';
import chunkArray from '@utils/helpers/chunkArray';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import ProductListingCardRow from './ProductListingCardRow';

interface Props {
  readonly productListingName: string;
  readonly results: ListingProductType[];
  readonly statusLabels: IProductListingStatusLabels;
  readonly pageSize: number;
  readonly pageNumber: number;
  readonly showOnlyKits?: boolean;
  readonly sortByParam?: string;
}

const ProductListingCardBlock = ({
  productListingName,
  pageSize,
  pageNumber,
  results,
  statusLabels,
  showOnlyKits,
  sortByParam,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const alreadyTrackedProducts = useRef<string[]>([]);
  const kitDropdownRefs = useRef<(IKitDropdownRef | null)[]>([]);
  const rowsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [activeCard, setActiveCard] = useState<IProduct | null>(null);
  const [activeColumnIndex, setActiveColumnIndex] = useState<number | null>(
    null
  );
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const [chunkedResults, setChunkedResults] = useState<ListingProductType[][]>(
    []
  );
  const nextFocusIndex = pageSize * pageNumber;
  const setCard = ({
    rowIndex,
    columnIndex,
  }: {
    rowIndex: number;
    columnIndex: number;
  }) => {
    setActiveCard(
      (chunkedResults[rowIndex]?.[columnIndex] as IProduct) || null
    );
  };

  useEffect(() => {
    if (isMobile) {
      const chunked = chunkArray(results, 1);
      setChunkedResults(chunked);
    } else {
      const chunked = chunkArray(results, 3);
      setChunkedResults(chunked);
    }
  }, [isMobile, results, activeCard, activeRowIndex, activeColumnIndex]);

  const onScrollRowIntoView = (rowIndex: number) => {
    rowsRef.current[rowIndex]?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (pageNumber === 0) {
      setActiveCard(null);
      setActiveRowIndex(null);
      setActiveColumnIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyKits, sortByParam, results]);

  const onDesktopKitDropdownButtonClick = (
    rowIndex: number,
    columnIndex: number
  ) => {
    const isActiveCard =
      activeColumnIndex === columnIndex && activeRowIndex === rowIndex;

    setCard({ rowIndex, columnIndex });
    setActiveColumnIndex(isActiveCard ? null : columnIndex);
    setActiveRowIndex(isActiveCard ? null : rowIndex);
    if (isActiveCard) onScrollRowIntoView(rowIndex);
  };

  const onMobileKitDropdownButtonClick = (rowIndex: number) => {
    setCard({ rowIndex, columnIndex: 0 });
    setActiveColumnIndex(activeColumnIndex === 0 ? null : 0);
    setActiveRowIndex(activeRowIndex === rowIndex ? null : rowIndex);
    onScrollRowIntoView(rowIndex);
  };

  const onListingProductsTracking = (currentProducts: ListingProductType[]) => {
    const newTrackedProducts = currentProducts.filter(
      (product) =>
        product.type === ListingCardTypeEnum.Product &&
        product.trackingData &&
        !alreadyTrackedProducts.current.includes(product.sku ?? '')
    ) as IProduct[];

    alreadyTrackedProducts.current = [
      ...alreadyTrackedProducts.current,
      ...newTrackedProducts.map((product) => product.sku ?? ''),
    ];
    if (newTrackedProducts.length === 0) return;

    updateDataLayer({
      ecommerce: null,
    });
    updateDataLayer({
      event: 'viewItemList',
      ecommerce: {
        item_list_name: productListingName,
        items: newTrackedProducts.map((product) =>
          mapToNewTrackingData(product.trackingData!, productListingName)
        ),
      },
    });
  };

  return (
    <div className={classNames('mt-12 space-y-8 md:space-y-20')}>
      {chunkedResults.map((rowProducts, rowIndex) => (
        <div
          key={rowProducts.map((product) => product.title).join(',')}
          className={classNames({
            'pb-14 lg:pb-[150px]': rowIndex === chunkedResults.length - 1,
          })}
          ref={(node) => {
            rowsRef.current[rowIndex] = node;
          }}
        >
          <ProductListingCardRow
            activeCard={activeCard}
            activeColumnIndex={activeColumnIndex}
            activeRowIndex={activeRowIndex}
            chunkedResults={chunkedResults}
            nextFocusIndex={nextFocusIndex}
            products={rowProducts}
            productListingName={productListingName}
            rowIndex={rowIndex}
            statusLabels={statusLabels}
            onDesktopKitDropdownButtonClick={onDesktopKitDropdownButtonClick}
            onMobileKitDropdownButtonClick={onMobileKitDropdownButtonClick}
            onListingProductsTracking={onListingProductsTracking}
          />

          <AnimatePresence mode="wait">
            {activeCard && rowIndex === activeRowIndex && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                onAnimationComplete={(animatedProps: { opacity: number }) => {
                  if (animatedProps.opacity === 1) {
                    kitDropdownRefs.current[rowIndex]?.scrollIntoView();
                  }
                }}
              >
                <KitDropdown
                  productListingName={productListingName}
                  ref={(node) => {
                    kitDropdownRefs.current[rowIndex] = node;
                  }}
                  statusLabels={statusLabels}
                  kitsTitleLabel={activeCard.kitsTitleLabel}
                  kitTitleLabel={activeCard.kitTitleLabel}
                  variations={activeCard.variations}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )) || null}
    </div>
  );
};

export default ProductListingCardBlock;
